import { useEffect } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import LZString from "lz-string";

import UFOSegment from "@atlaskit/react-ufo/segment";
import type AnalyticsWebClient from "@atlassiansox/analytics-web-client";

import { WacAnalyticsClientProvider } from "@/analytics/wac-analytics/analytics-client";
import { WacAnalyticsListener } from "@/analytics/wac-analytics/wac-analytics-listener-provider";
import { SitesContextProvider } from "@/components/sites-provider";
import { UserContextProvider } from "@/components/user-context/user-context-provider";
import { GupsProvider } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-gups";

import { Identity } from "./analytics/atl-analytics/identity";
import { InitializeAutoAtlAnalytics } from "./analytics/atl-analytics/initialize-auto-atl-analytics";
import { InitUFOV1 } from "./analytics/ufo/initUFO";
import { TemplateCategory } from "./components/confluence/templates/template-categories-search";
import { CrossFlowProvider } from "./components/cross-flow/services/index";
import { AppPricingData } from "./contentful/product-catalog";
import {
    BxpWebStargateClientProvider,
    getStargateBaseUrl
} from "./libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import {
    AvailableProductsResponse,
    CcpTransactionAccountData,
    ServerSideRequestStatus,
    UserProfile,
    UserProfileResponse
} from "./libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";
import { GrowthUnifiedProfile } from "./libraries/growth-unified-profile-client/unified-profile.schema";
import { AuxiaResponse } from "./pages/software/jira/service-management/auxia/get-auxia-jsm-paid-lp-treatment";
import { trackRouteUFOV1PageLoad } from "./ufo-v1";
import {
    WAC_PERSONA_PARAM,
    parseWacPersonaParam
} from "./uniform/components/personalization-container/src/personalization-url-utils";
import { UserSegmentTypes } from "./uniform/components/personalization-container/src/use-user-segments";
import {
    getUniformEnvironment,
    UniformRegenerationMetadata
} from "./uniform/utils";
import { createUniformContext } from "./uniform/utils/uniform-context";
import {
    ContentfulEnvironment,
    WACEnvironment,
    WAEContextProvider
} from "./uniform/utils/wae-context-provider";
import { Cookie } from "./utils/cookie";
import { checkFeatureFlagGate } from "./utils/feature-flags/check-feature-flag";
import { featureFlags } from "./utils/feature-flags/feature-flag-manifest";
import { GridOverlayWrapper } from "./utils/grid-overlay";
import { useAnchorLocalStorage } from "./utils/local-storage-hook";
import { LocationProvider } from "./utils/location-provider";
import { LoggedInUserProvider } from "./utils/logged-in-user-provider";
import { OneTrustProvider } from "./utils/onetrust-provider";
import { PricingContent } from "./utils/pricing-queries/get-pricing-content-gql";
import { SmartReCaptcha } from "./utils/smart-recaptcha";
import { SSRLoginStateProvider } from "./utils/ssr-login-state-provider";
import { isProduction } from "./utils/urls";
import { useDynamicTitleDescription } from "./utils/use-dynamic-title-description";
import { UserLocale } from "./utils/useGeoCurrency";
import { WacRefreshProvider } from "./utils/wac-refresh/wac-refresh-context";
import { parseRoute } from "./wac-routes";

import type {
    Template,
    TemplateCollection
} from "./content/templates/template.types";
import type { PreloadedQuery } from "react-relay";
import type { OperationType } from "relay-runtime";

export type CompositionMetrics = {
    compositionFetchStart?: number;
    compositionFetchEnd?: number;
    compositionCacheStatus?: string;
    ssrEndTime?: number;
};

export type PageProps = {
    auxiaResponse?: AuxiaResponse | null;
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    userProfile?: UserProfile;
    userProfileStatus?: ServerSideRequestStatus;
    userContext?: UserProfileResponse;
    userContextStatus?: ServerSideRequestStatus;
    loggedOutContext?: GrowthUnifiedProfile;
    loggedOutContextStatus?: ServerSideRequestStatus;
    loggedInContext?: GrowthUnifiedProfile;
    loggedInContextStatus?: ServerSideRequestStatus;
    countryCode?: UserLocale;
    templates?: Template[];
    partnerTemplates?: Template[];
    collections?: TemplateCollection[];
    categories?: TemplateCategory[];
    pricingContent?: PricingContent;
    teamType?: string;
    composition?: RootComponentInstance;
    compositionFetchStatusCode?: number;
    compositionRegenerationMetadata?: UniformRegenerationMetadata;
    isUniformPlayground?: boolean;
    isAllowPostMessage?: boolean;
    keyword?: string;
    mlTemplateIds?: string[];
    channel?: string;
    anchor?: string;
    subAnchor?: string;
    referrer?: string;
    track?: string;
    queryRef?: PreloadedQuery<OperationType>;
    hasCloudSessionTokenCookie?: boolean;
    transactionAccounts?: Array<CcpTransactionAccountData>;
    compositionMetrics?: CompositionMetrics;
    rawCompositionFile?: unknown;
    clusterTag?: string;
    utmMedium?: string;
    utmContent?: string;
    utmCampaign?: string;
    compositionStatusCode?: number;
};

export type PageWrapperProps = PageProps & {
    PageComponent: React.ComponentType<PageProps>;
    analyticsWebClient?: AnalyticsWebClient | null;
    location: Location;
    userAgent?: string;
};

/* Temporary code for debugging purposes - will be removed */
function getExperimentKeyValue(query: string, key: string): string | null {
    if (!query.startsWith("?")) {
        query = "?" + query;
    }

    const params = new URLSearchParams(query);
    return params.get(key);
}

const X_EXPERIMENTS_KEY = "X-Experiments-Key";

const checkEdgeCacheKeyMismatch = (
    analyticsWebClient: AnalyticsWebClient | null | undefined,
    wacWebTrafficAAGateValue: boolean
) => {
    if (analyticsWebClient) {
        const xExperimentsKey = Cookie.readCookie(X_EXPERIMENTS_KEY);
        if (xExperimentsKey) {
            const splitKey = xExperimentsKey.split("~")[1];
            if (!splitKey) return;
            const decodedExperimentsKey =
                LZString.decompressFromBase64(splitKey);
            const evaluatedValueInExpKey = getExperimentKeyValue(
                decodedExperimentsKey,
                featureFlags.wacWebTrafficAAGate.featureFlagName
            );
            const featureGatesAnonIdUsedInSSR =
                window?.featureGateBootstrapValues.identifiers
                    .analyticsAnonymousId;

            if (
                evaluatedValueInExpKey !== null &&
                evaluatedValueInExpKey !== undefined &&
                String(wacWebTrafficAAGateValue) !== evaluatedValueInExpKey
            ) {
                analyticsWebClient.sendOperationalEvent({
                    action: "mismatched",
                    actionSubject: "featureGate",
                    source: "layout",
                    attributes: {
                        featureGateName:
                            featureFlags.wacWebTrafficAAGate.featureFlagName,
                        route: window.location.pathname,
                        anonIdUsedInSSR: featureGatesAnonIdUsedInSSR,
                        anonIdInAnalyticsClient:
                            analyticsWebClient.getAnonymousId(),
                        experimentKeyValue: evaluatedValueInExpKey,
                        featureGateValue: String(wacWebTrafficAAGateValue),
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore - is static param
                        fallback: window.IS_STATIC ? "true" : "false"
                    }
                });
            }
        }
    }
};
/* */

// eslint-disable-next-line complexity
export const PageWrapper = (props: PageWrapperProps) => {
    const hasAutoAtlAnalytics =
        process.env["AUTO_ANALYTICS_ENABLED"] === "true";
    const pageViewProperties: { originProduct?: string } = {};
    const {
        locale,
        path,
        utmMedium,
        utmContent,
        utmCampaign,
        variant,
        subAnchor
    } = parseRoute(props.location);
    const atlassianUserId = Identity.getAtlassianUserId();
    useAnchorLocalStorage();

    // Hook to update title and description dynamically on client side
    useDynamicTitleDescription(props.location, props.queryRef);

    const isBrowser = () => typeof window !== "undefined";
    if (isBrowser()) {
        trackRouteUFOV1PageLoad(path);
        pageViewProperties.originProduct =
            "wac" +
            (window.location.pathname === "/"
                ? ""
                : window.location.pathname.replace(new RegExp("/", "g"), "-"));

        checkFeatureFlagGate(featureFlags.wacWebUseEsModulesScripts);

        // Trigger exposure for the edge lambda cache product discovery feature flag that is checked server side
        checkFeatureFlagGate(featureFlags.edgeLambdaCacheProductDiscovery);

        // AA gate, purely to observe the traffic volume in Statsig for logged in users
        //eslint-disable-next-line @typescript-eslint/no-unused-expressions
        atlassianUserId &&
            checkFeatureFlagGate(featureFlags.wacWebLoggedInTrafficAAGate);
    }

    const uniformContext = createUniformContext();
    uniformContext.update({
        quirks: {
            name: props.userProfile?.name ?? ""
        }
    });

    const personalizationValues: Record<string, string | string[]> = {};
    const clusterTagValue = variant || window.cmsPageVariant;
    const subAnchorValue = subAnchor || window.subAnchor;
    if (clusterTagValue) {
        personalizationValues[UserSegmentTypes.CLUSTER_TAG] = clusterTagValue;
    }
    if (subAnchorValue) {
        personalizationValues[UserSegmentTypes.SUB_ANCHOR] = subAnchorValue;
    }
    if (props.channel) {
        personalizationValues[UserSegmentTypes.CHANNEL] = props.channel;
    }

    if (!isProduction() && isBrowser()) {
        const queryParams = new URLSearchParams(window.location.search);
        const wacPersonaParam = queryParams.get(WAC_PERSONA_PARAM);
        if (wacPersonaParam) {
            const parsed = parseWacPersonaParam(
                decodeURIComponent(wacPersonaParam)
            );
            Object.assign(personalizationValues, parsed, {
                useOverrides: true
            });
        }
    }

    /* Use Effect below is temporary code for debugging purposes - will be removed */
    // useEdgeKeyMismatchHook(props.analyticsWebClient, wacWebTrafficAAGateValue);
    useEffect(() => {
        // AA gate, purely to observe the traffic volume in Statsig
        const wacWebTrafficAAGateValue = checkFeatureFlagGate(
            featureFlags.wacWebTrafficAAGate
        );

        checkEdgeCacheKeyMismatch(
            props.analyticsWebClient,
            wacWebTrafficAAGateValue
        );

        // Send operational event when static fallback is detected
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - parameters
        if (window.IS_STATIC && props.analyticsWebClient) {
            props.analyticsWebClient.sendOperationalEvent({
                action: "detected",
                actionSubject: "staticWACFallback",
                source: "page-wrapper-streaming",
                attributes: {
                    route: window.location.pathname,
                    userAgent: props.userAgent,
                    anonymousId: props.analyticsWebClient.getAnonymousId()
                }
            });
        }
    }, [props.analyticsWebClient, props.userAgent]);

    return (
        <OneTrustProvider isUniformPlayground={props.isUniformPlayground}>
            {process.env["NODE_ENV"] !== "production" && <GridOverlayWrapper />}
            <WacAnalyticsClientProvider
                analyticsWebClient={props.analyticsWebClient ?? null}
                location={props.location}
            >
                <InitUFOV1 />
                <LocationProvider location={props.location}>
                    <UFOSegment name="wac">
                        <SmartReCaptcha>
                            <BxpWebStargateClientProvider
                                baseUrl={getStargateBaseUrl()}
                            >
                                <SSRLoginStateProvider
                                    hasCloudSessionTokenCookie={
                                        props.hasCloudSessionTokenCookie ??
                                        false
                                    }
                                    userAgent={props.userAgent}
                                >
                                    <LoggedInUserProvider>
                                        <GupsProvider>
                                            <WacRefreshProvider>
                                                <WAEContextProvider
                                                    wacEnvironment={
                                                        process.env[
                                                            "CLIENT_LIBRARY_ENV"
                                                        ] as WACEnvironment
                                                    }
                                                    uniformEnvironment={getUniformEnvironment(
                                                        typeof window !==
                                                            "undefined"
                                                            ? window.location
                                                                  .href
                                                            : ""
                                                    )}
                                                    uniformProjectId={
                                                        process.env[
                                                            "UNIFORM_PROJECT_ID"
                                                        ]
                                                    }
                                                    isCanvas={
                                                        props.isUniformPlayground
                                                    }
                                                    isAllowPostMessage={
                                                        props.isAllowPostMessage
                                                    }
                                                    composition={
                                                        props.composition
                                                    }
                                                    contentfulEnvironment={
                                                        process.env[
                                                            "CONTENTFUL_ENVIRONMENT"
                                                        ] as ContentfulEnvironment
                                                    }
                                                    personalizationValues={
                                                        personalizationValues
                                                    }
                                                    uniformContext={
                                                        uniformContext
                                                    }
                                                >
                                                    <WacAnalyticsListener>
                                                        <CrossFlowProvider
                                                            locale={locale}
                                                        >
                                                            <SitesContextProvider>
                                                                <UserContextProvider
                                                                    location={
                                                                        props
                                                                            .location
                                                                            .href
                                                                    }
                                                                    referrer={
                                                                        props.referrer
                                                                    }
                                                                    meResponse={
                                                                        props.userProfile
                                                                    }
                                                                    meRequest={
                                                                        props.userProfileStatus
                                                                    }
                                                                    userProfileResponse={
                                                                        props.userContext
                                                                    }
                                                                    userProfileRequest={
                                                                        props.userContextStatus
                                                                    }
                                                                    loggedOutContext={
                                                                        props.loggedOutContext
                                                                    }
                                                                    loggedOutContextStatus={
                                                                        props.loggedOutContextStatus
                                                                    }
                                                                    loggedInContext={
                                                                        props.loggedInContext
                                                                    }
                                                                    loggedInContextStatus={
                                                                        props.loggedInContextStatus
                                                                    }
                                                                    transactionAccounts={
                                                                        props.transactionAccounts
                                                                    }
                                                                    utmCampaign={
                                                                        utmCampaign
                                                                    }
                                                                >
                                                                    <props.PageComponent
                                                                        {...props}
                                                                        utmMedium={
                                                                            utmMedium
                                                                        }
                                                                        utmContent={
                                                                            utmContent
                                                                        }
                                                                        utmCampaign={
                                                                            utmCampaign
                                                                        }
                                                                    />
                                                                </UserContextProvider>
                                                            </SitesContextProvider>
                                                            {/**
                                                             * InitializeAutoAtlAnalytics is used to auto-capture GTM events (previously handled
                                                             * by the atl-analytics GTM wrapper). The contents of that wrapper have been ported into
                                                             * this repository.
                                                             *
                                                             * Note: this will only attach auto-analytics to elements that exist in the page
                                                             * when this component is mounted, so it needs to be the last component to render.
                                                             *
                                                             * In its current state, this will only pick up the components that existed on
                                                             * after the first render of the above Component.
                                                             *
                                                             * You can call Initializer.autoTrack() in a useEffect or useLayoutEffect
                                                             * to attach listeners to any new buttons/links/etc. that were added dynamically.
                                                             */}
                                                            {hasAutoAtlAnalytics && (
                                                                <InitializeAutoAtlAnalytics
                                                                    pageViewProperties={
                                                                        pageViewProperties
                                                                    }
                                                                />
                                                            )}
                                                        </CrossFlowProvider>
                                                    </WacAnalyticsListener>
                                                </WAEContextProvider>
                                            </WacRefreshProvider>
                                        </GupsProvider>
                                    </LoggedInUserProvider>
                                </SSRLoginStateProvider>
                            </BxpWebStargateClientProvider>
                        </SmartReCaptcha>
                    </UFOSegment>
                </LocationProvider>
            </WacAnalyticsClientProvider>
        </OneTrustProvider>
    );
};
